import React, { useEffect, useRef } from 'react';
import useClosureEscape from 'hooks/useClosureEscape';

const proxies = new WeakMap();
const meta = new WeakMap();

function getProxy(ref, setValue) {
  const existingProxy = proxies.get(ref);
  if (existingProxy) return existingProxy;

  getRefMeta(ref);

  const proxy = new Proxy(ref, {
    get(target, prop, receiver) {
      const okay = new Set(['current', 'hasOwnProperty']);
      if (typeof prop === 'string' && okay.has(prop)) return Reflect.get(ref, prop, receiver);
      throw new Error(`Cannot get a property on the ref other than 'current.' Attempted to get '${String(prop)}'`);
    },
    set(target, prop, value, receiver) {
      if (prop === 'current') {
        setValue(value);
        return true;
      }
      throw new Error('Cannot set a property on the ref other than \'current\'');
    },
  });

  proxies.set(ref, proxy);

  return proxy;
}

function getRefMeta(ref) {
  const existingMeta = meta.get(ref);
  if (existingMeta) return existingMeta;

  const id = Math.random().toString(36).substring(2, 15);
  const createdAt = Date.now();
  meta.set(ref, { id, createdAt });
  return { id, createdAt };
}

/**
 * Benefits of state and ref. Has the up to date and closure-escaping properties of using a ref.
 * Has the reactivity of a state/triggers the re-render of the component when the value changes.
 * @param defaultValue
 * @returns
 */
export default function useRefState(
  defaultValue,
  {
    useState = true,
    isEqual = (a, b) => a === b,
  } = {},
) {
  let setState;
  if (useState) {
    const [state, setStateDirect] = React.useState(defaultValue);
    setState = setStateDirect;
  }

  const ref = useRef(defaultValue);
  const previousRef = useRef(undefined);
  const lastDifferentRef = useRef(undefined);

  const listeners = useRef(new Set());

  function setValue(value) {
    const previous = ref.current;
    previousRef.current = previous;
    if (useState) setState(value);
    ref.current = typeof value === 'function' ? value(ref.current) : value;
    if (!isEqual(ref.current, previous)) {
      lastDifferentRef.current = previous;
      listeners.current.forEach((listener) => listener(ref.current, previous));
    }
    return ref.current;
  }

  const refProxy = getProxy(ref, setValue);

  function listen(listener) {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }

  function useListen(listener) {
    const escaped = useClosureEscape(listener);

    useEffect(() => {
      const unlisten = listen(escaped);
      return () => {
        unlisten();
      };
    }, []);
  }

  return {
    setValue,
    get value() { return ref.current; },
    getValue() { return ref.current; },
    getPrevious() { return previousRef.current; },
    getLastDifferent() { return lastDifferentRef.current; },
    ref: refProxy,
    listen,
    unlisten: (listener) => listeners.current.delete(listener),
    unlistenAll: () => listeners.current.clear(),
    useListen,

    get meta() { return { ...getRefMeta(ref), listeners: listeners.current.size }; },
  };
}

export function useMemoRefState(
  fn,
  dependencies,
  {
    useState,
  } = {},
) {
  const memoized = useRefState(fn(), { useState: useState === true });
  useEffect(() => {
    memoized.setValue(fn());
  }, dependencies);

  return memoized;
}
